import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Lightbulb, Users, Clock, Trophy,
  BookOpen, Target, CheckCircle,
  ChevronRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackPageView } from '../utils/trackingUtils';
import winners2024 from '../assets/winners2024.jpeg';
// import CountdownTimer from '../components/CountdownTimer';
import InterestFormModal from '../components/modals/InterestFormModal';
import DatePreferenceModal from '../components/modals/DatePreferenceModal';
import { useOnlineClasses } from '../contexts/OnlineClassContext';


// No fallback data - show empty state when API fails

// Transform OnlineClass to the format expected by HomePage
const transformOnlineClassForHomePage = (onlineClass: any) => {
  const formatDateString = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = { 
      month: 'long', 
      day: 'numeric'
    };
    return date.toLocaleDateString('en-US', options);
  };

  const formatTimeString = (startDate: Date, endDate?: Date): string => {
    const startTime = startDate.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    if (endDate) {
      const endTime = endDate.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
      return `${startTime} - ${endTime} EST`;
    }
    
    return `${startTime} EST`;
  };

  return {
    name: onlineClass.title,
    fullName: onlineClass.title,
    date: formatDateString(onlineClass.startDate),
    time: formatTimeString(onlineClass.startDate, onlineClass.endDate),
    link: "/VBC-Online",
    description: onlineClass.shortDescription || onlineClass.description,
    price: onlineClass.price
  };
};

// Program comparison data
const programComparison = [
  {
    title: "VBC-Online",
    features: [
      "Live, Instructor-Led Class",
      "Fun, gamified introduction",
      "Live online competition",
      "Run a virtual company, compete, collaborate",
      "Perfect for first-timers or extracurricular learners"
    ],
    link: "",
    buttonText: "Join the Next Class"
  },
  {
    title: "Virtual Business Challenge",
    features: [
      "Full Business Learning Program",
      "In-depth curriculum + skills portfolio",
      "Multi-week guided program",
      "Build projects, apply strategies, develop real business plans",
      "Ideal for homeschoolers wanting a structured course"
    ],
    link: "",
    buttonText: "Coming soon.."
  }
];

// Benefits data
const benefitPoints = [
  {
    title: "Learn by Doing",
    description: "Teens run virtual businesses, solve challenges, and apply concepts hands-on.",
    icon: <BookOpen className="w-6 h-6" />
  },
  {
    title: "Flexible Learning",
    description: "Choose from self-paced, live classes, or group-based challenges.",
    icon: <Clock className="w-6 h-6" />
  },
  {
    title: "Engaging & Competitive",
    description: "Friendly competitions inspire motivation and social learning.",
    icon: <Trophy className="w-6 h-6" />
  },
  {
    title: "Future-Ready Skills",
    description: "Build a portfolio, develop leadership, and boost college/career readiness.",
    icon: <Target className="w-6 h-6" />
  },
  {
    title: "No Extra Work for Parents",
    description: "Programs are guided, structured, and easy to integrate.",
    icon: <CheckCircle className="w-6 h-6" />
  }
];

export default function AlternativeHomeschoolPage() {
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [showDatePreferenceModal, setShowDatePreferenceModal] = useState(false);
  const [interestModalTitle, setInterestModalTitle] = useState('Express Your Interest');
  const [interestModalSubtitle, setInterestModalSubtitle] = useState('We\'ll notify you about upcoming classes and special offers.');

  // Use online class context - upcomingClasses is already filtered to show only featured classes
  const { upcomingClasses } = useOnlineClasses();

  // Transform API data - don't use fallback data
  // Note: upcomingClasses already contains only featured classes (filtered in context)
  const upcomingClassesData = upcomingClasses.length > 0
    ? upcomingClasses.slice(0, 3).map(transformOnlineClassForHomePage)
    : [];

  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView('HOME_HOMESCHOOL', 'alternative-homeschool-page');
  }, []);

  const openInterestModal = (title?: string, subtitle?: string) => {
    if (title) setInterestModalTitle(title);
    if (subtitle) setInterestModalSubtitle(subtitle);
    setShowInterestModal(true);
  };

  const closeInterestModal = () => {
    setShowInterestModal(false);
  };

  const closeDatePreferenceModal = () => {
    setShowDatePreferenceModal(false);
  };

  return (
    <>
      <Helmet>
        <title>Virtual Business Programs for Homeschoolers - EmploSkills</title>
        <meta 
          name="description" 
          content="Hands-on, project-based business and entrepreneurship programs for homeschool teens aged 14–18."
        />
      </Helmet>

      {/* Interest Form Modal */}
      <InterestFormModal 
        isOpen={showInterestModal}
        onClose={closeInterestModal}
        title={interestModalTitle}
        subtitle={interestModalSubtitle}
      />

      {/* Date Preference Modal */}
      <DatePreferenceModal
        isOpen={showDatePreferenceModal}
        onClose={closeDatePreferenceModal}
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary to-blue-600 text-white py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Emplo's Virtual Business Challenge
            
              </h1>
              <h2 className="text-xl md:text-2xl mb-6 text-blue-100">
              Empower Your Teen with Real-World Business Skills – From Home
              </h2>
              <p className="text-lg mb-8">
              Project-based business and entrepreneurship programs for homeschool teens aged 14–18. 
                Whether your teen wants a fun, social and engaging introduction to business, marketing, and entrepreneurship, or a deep-dive curriculum designed to help students develop their business and entrepreneurship skills, we've got flexible options that fit your homeschool schedule.
              </p>
              <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
                {/* <Link 
                  to="/virtual-business-challenge"
                  className="bg-white text-primary px-8 py-4 rounded-full font-semibold hover:bg-blue-50 transition text-lg"
                >
                  Explore the VBC
                </Link> */}
                <Link 
                  to="/VBC-Online#course-details"
                  className="bg-blue-700 text-white border border-white px-8 py-4 rounded-full font-semibold hover:bg-blue-800 transition text-lg"
                >
                  Learn More
                </Link>
                {/* <a 
                  href={
                    upcomingClasses.length > 0 && upcomingClasses.some(cls => {
                      const now = new Date();
                      return cls.startDate > now && (!cls.maxStudents || !cls.currentEnrollment || cls.currentEnrollment < cls.maxStudents);
                    })
                      ? `https://platform.emploskills.com/online-class/${upcomingClasses.find(cls => {
                          const now = new Date();
                          return cls.startDate > now && (!cls.maxStudents || !cls.currentEnrollment || cls.currentEnrollment < cls.maxStudents);
                        })?.id}/preview`
                      : `https://platform.emploskills.com/`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-primary px-8 py-4 rounded-full font-semibold hover:bg-blue-50 transition text-lg"
                >
                  Secure Your Spot
                </a> */}
              </div>
            </div>
            
            <div className="hidden lg:block">
              <img 
                src={winners2024} 
                alt="VBC Winners 2024" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
      </section>

      {/* What is the VBC-Online Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">
              What is the Virtual Business Class (VBC-Online)?
            </h2>
            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-gray-700 mb-8">
                VBC-Online is a live, 6-hour interactive business simulation competition where teens run virtual companies, make real-time business decisions, and compete with peers. Perfect for homeschoolers looking for engaging, hands-on learning with immediate results and no long-term commitment.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
                <div className="bg-gray-50 p-6 rounded-xl shadow-sm">
                  <Lightbulb className="w-8 h-8 text-blue-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Simple & Engaging</h3>
                  <p className="text-gray-600">Business concepts are made real, engaging, and fun through gamified learning that teens love.</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl shadow-sm">
                  <Users className="w-8 h-8 text-green-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Learn by Doing</h3>
                  <p className="text-gray-600">Students take on the role of business owners in an interactive, competitive simulation.</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl shadow-sm">
                  <Trophy className="w-8 h-8 text-purple-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Competitive Edge</h3>
                  <p className="text-gray-600">Students compete for the top spot while learning fundamental business skills.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is the VBC Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
            What Is the Virtual Business Challenge?
          </h2>
          <p className="text-lg text-gray-700 mb-12 text-center max-w-4xl mx-auto">
            The Virtual Business Challenge (VBC) is an online, project-based module based program that teaches teens real-world business skills through interactive simulations, project assignments, skills assessments and team competitions where students receive project based challenges.
          </p>

          <h3 className="text-2xl font-bold mb-8 text-center">
            Compare Our Two Program Tracks:
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {programComparison.map((program, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold mb-6 text-primary">{program.title}</h3>
                <ul className="space-y-4 mb-8">
                  {program.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                {program.link ? (
                  <Link 
                    to={program.link} 
                    className="inline-block bg-primary text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-700 transition"
                  >
                    {program.buttonText}
                  </Link>
                ) : program.buttonText === "Join the Next Class" ? (
                  <button
                    onClick={() => {
                      const upcomingSection = document.getElementById('upcoming-classes');
                      if (upcomingSection) {
                        upcomingSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="inline-block bg-primary text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-700 transition"
                  >
                    {program.buttonText}
                  </button>
                ) : (
                  <button
                    onClick={() => openInterestModal('Interest in Full VBC Program', 'We\'ll notify you when the full Virtual Business Challenge program launches.')}
                    className="inline-block bg-primary text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-700 transition"
                  >
                    {program.buttonText}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Families Love It */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
            Why Homeschool Families Love It
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefitPoints.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="bg-blue-100 p-3 rounded-full flex-shrink-0">
                  {benefit.icon}
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Facebook Community Section */}
      <section className="py-16 bg-gradient-to-br bg-primary to-blue-600 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Join Our Growing Community
              </h2>
              <p className="text-xl mb-8 text-blue-100">
                Connect with like-minded homeschool families, get exclusive discounts, and stay updated on the latest homeschoolbusiness education opportunities.
              </p>
              {/* <div className="bg-white/10 rounded-xl p-6 mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold">Community</h3>
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">Save 40%</span> 
                </div>
                <p className="mb-4">Join our Facebook community to receive your exclusive discount code and save on all our programs!</p>
                <div className="flex items-center space-x-2 text-sm text-blue-100">
                  
                  <span>Regular price: $99</span> 
                  <span>→</span>
                  <span className="font-semibold text-white">Community price: $59</span>
                </div>
              </div> */}
             <a 
                href="https://www.facebook.com/groups/1163931521921469"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-white text-blue-600 px-6 py-3 rounded-full font-semibold hover:bg-blue-50 transition-colors"
              >
                Join Our Community
                <ChevronRight className="w-5 h-5 ml-2" />
              </a>
            </div>
            <div className="space-y-6">
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-4">Community Benefits</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="bg-blue-400/20 p-2 rounded-lg mr-4">
                      <Users className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-medium">Network & Connect</h4>
                      <p className="text-blue-100 text-sm">Meet other homeschool families passionate about business education</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-blue-400/20 p-2 rounded-lg mr-4">
                      <Trophy className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-medium">Exclusive Content</h4>
                      <p className="text-blue-100 text-sm">Get early access to new programs and special events</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-blue-400/20 p-2 rounded-lg mr-4">
                      <Lightbulb className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-medium">Learning Resources</h4>
                      <p className="text-blue-100 text-sm">Access free business education tips and resources</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Classes */}
      <section id="upcoming-classes" className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
            Upcoming Virtual Business Classes (VBC-Online)
          </h2>
          <p className="text-lg text-gray-700 mb-12 text-center max-w-4xl mx-auto">
            Join our live, instructor-led classes and see what business learning can really be like!
          </p>
          
          <div className="overflow-x-auto">
            {upcomingClassesData.length > 0 ? (
              <table className="w-full border-collapse mb-8">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="py-4 px-6 text-left font-semibold">Class Name</th>
                    <th className="py-4 px-6 text-left font-semibold">Date</th>
                    <th className="py-4 px-6 text-left font-semibold">Time</th>
                    <th className="py-4 px-6 text-left font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingClassesData.map((classItem: any, index: number) => (
                    <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <div className="flex items-center">
                          <span className="font-medium">{classItem.name}</span>
                          {classItem.price === 0 && (
                            <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium ml-2 animate-pulse">
                              FREE
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6">{classItem.date}</td>
                      <td className="py-4 px-6">{classItem.date === "TBA" ? "TBA" : classItem.time}</td>
                      <td className="py-4 px-6">
                        {classItem.date === "TBA" ? (
                          <button 
                            onClick={() => openInterestModal(
                              `Interest in ${classItem.name} Class`, 
                              `We'll notify you when ${classItem.name} becomes available.`
                            )}
                            className="inline-block bg-gray-200 text-gray-800 px-4 py-2 rounded-full font-medium hover:bg-gray-300 transition text-sm"
                          >
                            Show Interest
                          </button>
                        ) : (
                          <Link 
                            to={`${classItem.link}#course-details`} 
                            className="inline-block bg-primary text-white px-4 py-2 rounded-full font-medium hover:bg-blue-700 transition text-sm"
                          >
                            Register
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <p className="text-gray-500 text-lg">No upcoming classes</p>
              </div>
            )}
          </div>
          
          <p className="text-center mb-6">
            Need a different date?{" "}
            <button 
              onClick={() => setShowDatePreferenceModal(true)}
              className="text-blue-600 font-medium hover:text-blue-800 transition-colors"
            >
              Click here to show interest.
            </button>
          </p>
        </div>
      </section>

      {/* Call to Action */}
      {/* <section className="py-16 bg-gradient-to-br bg-primary to-blue-600  text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-10">Choose your path:</p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-6">
            <button 
              onClick={() => {
                const upcomingSection = document.getElementById('upcoming-classes');
                if (upcomingSection) {
                  upcomingSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="bg-white text-primary px-8 py-4 rounded-full font-semibold hover:bg-blue-50 transition text-lg"
            >
              Join a Class
            </button>
            <a 
              href={
                upcomingClasses.length > 0 && upcomingClasses.some(cls => {
                  const now = new Date();
                  return cls.startDate > now && (!cls.maxStudents || !cls.currentEnrollment || cls.currentEnrollment < cls.maxStudents);
                })
                  ? `https://platform.emploskills.com/online-class/${upcomingClasses.find(cls => {
                      const now = new Date();
                      return cls.startDate > now && (!cls.maxStudents || !cls.currentEnrollment || cls.currentEnrollment < cls.maxStudents);
                    })?.id}/preview`
                  : `https://platform.emploskills.com/`
              }
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-blue-700 text-white rounded-full font-semibold hover:bg-blue-800 transition-colors"
            >
              Secure Your Spot
            </a>
          </div>
          <p className="mt-6 text-blue-100">
            Flexible, fun learning options for every homeschool family
          </p>
        </div>
      </section> */}

      {/* Countdown Timer */}

            {/* <section className="py-12 md:py-20 px-4 md:px-8 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-8">
            Next Open Virtual Business Challenge Class Starting Soon!
          </h2>
          <div className="mb-12">
            <CountdownTimer />
          </div>


          <div className="bg-primary text-white p-8 rounded-xl mt-12">
            <h3 className="text-2xl font-bold mb-4">
              Join The Next Open VBC Online
            </h3>
            <p className="mb-6">
              VBC Online can transform your homeschooling experience. Join our next live session!
            </p>
            <div className="bg-white/10 p-4 rounded-lg mb-6">
              <div className="font-semibold mb-1">Next Class:</div>
              <div>Wednesday 23rd of April 2025 at 16:00 PM EST</div>
            </div>
            <div className="w-full flex justify-center space-x-4">
              <Link 
                to="/VBC-Online#course-details"
                className="px-6 py-3 bg-white text-primary rounded-full font-semibold hover:bg-blue-50 transition-colors"
              >
                Learn More
              </Link>
              <a 
                href={
                  upcomingClasses.length > 0 && upcomingClasses.some(cls => {
                    const now = new Date();
                    return cls.startDate > now && (!cls.maxStudents || !cls.currentEnrollment || cls.currentEnrollment < cls.maxStudents);
                  })
                    ? `https://platform.emploskills.com/online-class/${upcomingClasses.find(cls => {
                        const now = new Date();
                        return cls.startDate > now && (!cls.maxStudents || !cls.currentEnrollment || cls.currentEnrollment < cls.maxStudents);
                      })?.id}/preview`
                    : `https://platform.emploskills.com/`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-blue-700 text-white rounded-full font-semibold hover:bg-blue-800 transition-colors"
              >
                Secure Your Spot
              </a>
            </div>
          </div>
        </div>
      </section> */}
    </>
  );
} 